import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner, ErrorMessage, EmptyState } from './CommonUI';
import SetCard from './SetCard';
import Modal from './Modal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const SubThemes = () => {
    const [subThemes,        setSubThemes]        = useState([]);
    const [sets,             setSets]             = useState([]);
    const [totalSets,        setTotalSets]        = useState(0); // true total from API
    const [selectedSets,     setSelectedSets]     = useState({});
    // Persistent ownership: { [set_num]: { inCollection: number, inWishlist: boolean } }
    const [collectionStatus, setCollectionStatus] = useState({});
    const [page,             setPage]             = useState(1);
    const [isLoading,        setIsLoading]        = useState(true);
    const [isLoadingMore,    setIsLoadingMore]    = useState(false);
    const [hasMore,          setHasMore]          = useState(true);
    const [parentThemeName,  setParentThemeName]  = useState('');
    const [parentThemeImage, setParentThemeImage] = useState('');
    const [error,            setError]            = useState(null);
    const [isModalOpen,      setIsModalOpen]      = useState(false);
    const [modalContent,     setModalContent]     = useState({});
    const [filterOptions,    setFilterOptions]    = useState({
        decade: 'all',
        pieces: 'all',
        year:   'all',
        sort:   'year_desc'
    });
    const [showFilters,  setShowFilters]  = useState(false);
    const [filteredSets, setFilteredSets] = useState([]);
    const [activeView,   setActiveView]   = useState('grid');
    const [searchQuery,  setSearchQuery]  = useState('');

    const { themeId } = useParams();
    const navigate    = useNavigate();
    const { user }    = useAuth();

    const themePageTitle = useMemo(() => parentThemeName || 'Themes', [parentThemeName]);
    useDocumentTitle(themePageTitle);

    // -------------------------------------------------------------------------
    // Data fetching
    // -------------------------------------------------------------------------

    // Fetch sub-themes + parent theme info on theme change
    useEffect(() => {
        const fetchSubThemes = async () => {
            setIsLoading(true);
            try {
                axios.defaults.withCredentials = true;
                const [subThemesRes, themeInfoRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/get_sub_themes.php?parent_id=${themeId}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/get_theme_by_id.php?id=${themeId}`)
                ]);

                setSubThemes(subThemesRes.data);

                if (themeInfoRes.data?.name) {
                    setParentThemeName(themeInfoRes.data.name);
                } else if (subThemesRes.data.length > 0) {
                    setParentThemeName(subThemesRes.data[0].parent_theme_name);
                }

                if (subThemesRes.data.length > 0 && subThemesRes.data[0].parent_theme_image) {
                    setParentThemeImage(subThemesRes.data[0].parent_theme_image);
                }
            } catch {
                setError('Failed to load theme data. Please try again later.');
            }
            setIsLoading(false);
        };

        fetchSubThemes();

        // Reset per-theme state
        setSets([]);
        setTotalSets(0);
        setCollectionStatus({});
        setPage(1);
        setHasMore(true);
        setFilterOptions({ decade: 'all', pieces: 'all', year: 'all', sort: 'year_desc' });
        setSearchQuery('');
    }, [themeId]);

    // Fetch paginated sets
    useEffect(() => {
        if (!hasMore) return;

        const fetchSets = async () => {
            setIsLoadingMore(true);
            try {
                const ts = Date.now();
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/get_lego_sets.php?theme_id=${themeId}&page=${page}&_=${ts}`
                );

                if (!res.data.sets) {
                    setIsLoadingMore(false);
                    return;
                }

                // Merge new sets (deduplicate)
                setSets(prev => {
                    const existing = new Set(prev.map(s => s.set_num));
                    const newSets  = res.data.sets.filter(s => !existing.has(s.set_num));
                    return [...prev, ...newSets];
                });

                // Update total count from first page response
                if (page === 1 && res.data.total != null) {
                    setTotalSets(Number(res.data.total));
                }

                setHasMore(res.data.hasMore);

                // Merge new ownership data into collectionStatus
                setCollectionStatus(prev => {
                    const updated = { ...prev };
                    res.data.sets.forEach(set => {
                        const inCollection = Number(set.in_collection || 0);
                        const inWishlist   = Boolean(Number(set.in_wishlist || 0));
                        // Only seed if not already overridden by a local add
                        if (!updated[set.set_num]) {
                            if (inCollection > 0 || inWishlist) {
                                updated[set.set_num] = { inCollection, inWishlist };
                            }
                        }
                    });
                    return updated;
                });
            } catch {
                setError('Failed to load sets. Please try again later.');
                setHasMore(false);
            }
            setIsLoadingMore(false);
        };

        fetchSets();
    }, [page, themeId, hasMore]);

    // -------------------------------------------------------------------------
    // Filtering + sorting
    // -------------------------------------------------------------------------

    useEffect(() => {
        let results = [...sets];

        if (filterOptions.decade !== 'all') {
            const decade = parseInt(filterOptions.decade);
            results = results.filter(s => {
                const y = parseInt(s.year);
                return y >= decade && y < decade + 10;
            });
        }

        if (filterOptions.pieces !== 'all') {
            const [min, max] = filterOptions.pieces.split('-').map(Number);
            results = results.filter(s => {
                const p = parseInt(s.num_parts);
                return max ? p >= min && p <= max : p >= min;
            });
        }

        if (filterOptions.year !== 'all') {
            const year = parseInt(filterOptions.year);
            results = results.filter(s => parseInt(s.year) === year);
        }

        const [sortField, sortDir] = filterOptions.sort.split('_');
        results.sort((a, b) => {
            if (sortField === 'name') {
                return sortDir === 'asc'
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            }
            const vA = sortField === 'pieces' ? parseInt(a.num_parts) : parseInt(a.year);
            const vB = sortField === 'pieces' ? parseInt(b.num_parts) : parseInt(b.year);
            return sortDir === 'asc' ? vA - vB : vB - vA;
        });

        setFilteredSets(results);
    }, [sets, filterOptions]);

    // -------------------------------------------------------------------------
    // Infinite scroll
    // -------------------------------------------------------------------------

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop
                >= document.documentElement.offsetHeight - 500
                && !isLoadingMore && hasMore
            ) {
                setPage(p => p + 1);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isLoadingMore, hasMore]);

    // -------------------------------------------------------------------------
    // Selection helpers
    // -------------------------------------------------------------------------

    const handleQuantityChange = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        setSelectedSets(prev => ({ ...prev, [setNum]: quantity }));
    }, []);

    const toggleSelectSet = useCallback((setNum) => {
        if (!user) {
            setModalContent({
                title:       'Sign In Required',
                message:     'You need to be signed in to add sets to your collection.',
                confirmText: 'Sign In',
                onConfirm:   () => navigate('/login')
            });
            setIsModalOpen(true);
            return;
        }
        setSelectedSets(prev => {
            const n = { ...prev };
            if (n[setNum] !== undefined) { delete n[setNum]; } else { n[setNum] = 1; }
            return n;
        });
    }, [user, navigate]);

    const clearSelectedSets  = ()  => setSelectedSets({});
    const getSelectedSetCount = () => Object.keys(selectedSets).length;

    // -------------------------------------------------------------------------
    // Add handlers (single or bulk)
    // -------------------------------------------------------------------------

    const updateStatusLocal = useCallback((setNum, fields) => {
        setCollectionStatus(prev => {
            const cur = prev[setNum] ?? { inCollection: 0, inWishlist: false };
            return { ...prev, [setNum]: { ...cur, ...fields } };
        });
    }, []);

    // Fire-and-forget: remove a set from the wishlist server-side after it's been collected
    const removeFromWishlistAfterCollect = useCallback((setNum) => {
        if (!user?.user_id) return;
        axios.post(`${process.env.REACT_APP_API_URL}/remove_set_from_wishlist.php`, {
            user_id: user.user_id,
            set_num: setNum,
        }).then(res => {
            if (res.data.success) updateStatusLocal(setNum, { inWishlist: false });
        }).catch(() => {});
    }, [user, updateStatusLocal]);

    // quantity is provided when called from SetCard (single set); null when called from bulk bar
    const addToCollection = useCallback((setNum = null, quantity = null, e = null) => {
        if (e) e.stopPropagation();
        const setsToAdd = setNum
            ? [{ setNum, quantity: quantity ?? selectedSets[setNum] }]
            : Object.entries(selectedSets).map(([sn, qty]) => ({ setNum: sn, quantity: qty }));

        // Capture wishlist state for each set before the async call
        const wishlistedNums = setsToAdd
            .filter(({ setNum: sn }) => collectionStatus[sn]?.inWishlist)
            .map(({ setNum: sn }) => sn);

        axios.post(`${process.env.REACT_APP_API_URL}/add_to_collection.php`, { sets: setsToAdd })
            .then(res => {
                if (res.data.success) {
                    setsToAdd.forEach(({ setNum: sn, quantity: qty }) => {
                        const wasInWishlist = wishlistedNums.includes(sn);
                        updateStatusLocal(sn, {
                            inCollection: (collectionStatus[sn]?.inCollection ?? 0) + qty,
                            ...(wasInWishlist ? { inWishlist: false } : {}),
                        });
                        if (wasInWishlist) removeFromWishlistAfterCollect(sn);
                    });
                    if (setNum) {
                        setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
                    } else {
                        setSelectedSets({});
                    }
                } else {
                    setError('Failed to add sets to collection.');
                }
            })
            .catch(() => setError('An error occurred while adding to collection.'));
    }, [selectedSets, collectionStatus, updateStatusLocal, removeFromWishlistAfterCollect]);

    const addToWishlist = useCallback((setNum = null, quantity = null, e = null) => {
        if (e) e.stopPropagation();
        const setsToAdd = setNum
            ? [{ setNum, quantity: quantity ?? selectedSets[setNum] }]
            : Object.entries(selectedSets).map(([sn, qty]) => ({ setNum: sn, quantity: qty }));

        axios.post(`${process.env.REACT_APP_API_URL}/add_to_wishlist.php`, { sets: setsToAdd })
            .then(res => {
                if (res.data.success) {
                    setsToAdd.forEach(({ setNum: sn }) => updateStatusLocal(sn, { inWishlist: true }));
                    if (setNum) {
                        setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
                    } else {
                        setSelectedSets({});
                    }
                } else {
                    setError('Failed to add sets to wishlist.');
                }
            })
            .catch(() => setError('An error occurred while adding to wishlist.'));
    }, [selectedSets, updateStatusLocal]);

    // -------------------------------------------------------------------------
    // Filter helpers
    // -------------------------------------------------------------------------

    const getYearOptions    = () => Array.from(new Set(sets.map(s => s.year))).sort((a, b) => b - a);
    const getDecadeOptions  = () => {
        if (!sets.length) return [];
        const years    = sets.map(s => parseInt(s.year));
        const start    = Math.floor(Math.min(...years) / 10) * 10;
        const end      = Math.floor(Math.max(...years) / 10) * 10;
        const decades  = [];
        for (let d = start; d <= end; d += 10) decades.push(d);
        return decades.sort((a, b) => b - a);
    };

    const handleFilterChange = (name, value) => setFilterOptions(prev => ({ ...prev, [name]: value }));
    const resetFilters       = () => {
        setFilterOptions({ decade: 'all', pieces: 'all', year: 'all', sort: 'year_desc' });
        setSearchQuery('');
    };

    const hasActiveFilter = () =>
        filterOptions.decade !== 'all' ||
        filterOptions.pieces !== 'all' ||
        filterOptions.year   !== 'all' ||
        filterOptions.sort   !== 'year_desc';

    const activeFilterCount = () =>
        (filterOptions.decade !== 'all' ? 1 : 0) +
        (filterOptions.pieces !== 'all' ? 1 : 0) +
        (filterOptions.year   !== 'all' ? 1 : 0) +
        (filterOptions.sort   !== 'year_desc' ? 1 : 0);

    // Navigate to global search page
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
        }
    };

    // -------------------------------------------------------------------------
    // Render helpers
    // -------------------------------------------------------------------------

    const handleImageError = e => { e.target.src = '/images/lego_piece_questionmark.png'; };

    const renderThemeHeader = () => (
        <div className="mb-8 bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-lg overflow-hidden">
            <div className="relative py-8 px-6 md:px-10">
                <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
                    <div className="md:max-w-2xl">
                        <div className="mb-4 flex items-center">
                            <button
                                onClick={() => navigate('/themes')}
                                className="mr-4 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                            >
                                <FontAwesomeIcon icon="arrow-left" />
                            </button>
                            <h1 className="text-3xl md:text-4xl font-bold text-white">{parentThemeName}</h1>
                        </div>

                        <p className="text-red-100 mb-4 text-lg">
                            Explore {parentThemeName} sets and sub-themes to add to your collection.
                        </p>

                        <div className="flex flex-wrap gap-3">
                            <div className="inline-flex items-center px-3 py-1 bg-red-500/50 text-white rounded-full text-sm">
                                <FontAwesomeIcon icon="cubes" className="mr-2" />
                                <span>
                                    {totalSets > 0
                                        ? `${totalSets.toLocaleString()} Sets`
                                        : sets.length > 0 ? `${sets.length}+ Sets` : 'Loading...'}
                                </span>
                            </div>

                            {subThemes.length > 0 && (
                                <div className="inline-flex items-center px-3 py-1 bg-red-500/50 text-white rounded-full text-sm">
                                    <FontAwesomeIcon icon="folder" className="mr-2" />
                                    <span>{subThemes.length} Sub-theme{subThemes.length !== 1 ? 's' : ''}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {parentThemeImage && (
                        <div className="hidden md:block w-36 h-36 bg-white rounded-xl p-2 shadow-lg transform rotate-3">
                            <img
                                src={parentThemeImage}
                                alt={parentThemeName}
                                className="w-full h-full object-contain"
                                onError={handleImageError}
                            />
                        </div>
                    )}
                </div>

                <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'url(/images/lego_pattern_bg.png)', backgroundSize: '200px' }} />
                <img src="/images/2x1-brick-yellow.png" alt="" aria-hidden="true"
                    className="absolute top-4 right-8 w-20 h-20 opacity-25 transform -rotate-6 pointer-events-none select-none object-contain" draggable="false" />
                <img src="/images/1x1-brick-red.png" alt="" aria-hidden="true"
                    className="absolute bottom-6 left-10 w-16 h-16 transform rotate-12 opacity-30 pointer-events-none select-none object-contain" draggable="false" />
            </div>
        </div>
    );

    const renderSearchAndFilters = () => {
        const yearOptions   = getYearOptions();
        const decadeOptions = getDecadeOptions();

        return (
            <div className="mb-8 bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="p-4 md:p-6 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Global search */}
                        <div className="relative flex-grow max-w-2xl">
                            <form onSubmit={handleSearch} className="relative w-full">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon="search" className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search sets by name or number..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                                <button
                                    type="submit"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    <FontAwesomeIcon icon="search" />
                                </button>
                            </form>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Filter toggle */}
                            <button
                                onClick={() => setShowFilters(v => !v)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                                    showFilters || hasActiveFilter()
                                        ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <FontAwesomeIcon icon="filter" />
                                <span className="font-medium">Filters</span>
                                {hasActiveFilter() && (
                                    <span className="ml-1 w-5 h-5 bg-white text-red-600 rounded-full text-xs flex items-center justify-center font-bold">
                                        {activeFilterCount()}
                                    </span>
                                )}
                            </button>

                            {/* Grid / list toggle */}
                            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setActiveView('grid')}
                                    className={`px-3 py-2 ${activeView === 'grid' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <FontAwesomeIcon icon="th" />
                                </button>
                                <button
                                    onClick={() => setActiveView('list')}
                                    className={`px-3 py-2 ${activeView === 'list' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <FontAwesomeIcon icon="list" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter panel */}
                {showFilters && (
                    <div className="p-4 md:p-6 border-b border-gray-200 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1 font-medium">Sort By</label>
                                <select value={filterOptions.sort} onChange={e => handleFilterChange('sort', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                                    <option value="year_desc">Newest First</option>
                                    <option value="year_asc">Oldest First</option>
                                    <option value="name_asc">Name (A-Z)</option>
                                    <option value="name_desc">Name (Z-A)</option>
                                    <option value="pieces_desc">Most Pieces</option>
                                    <option value="pieces_asc">Fewest Pieces</option>
                                </select>
                            </div>

                            {decadeOptions.length > 0 && (
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1 font-medium">Decade</label>
                                    <select value={filterOptions.decade} onChange={e => handleFilterChange('decade', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                                        <option value="all">All Decades</option>
                                        {decadeOptions.map(d => <option key={d} value={d}>{d}s</option>)}
                                    </select>
                                </div>
                            )}

                            {yearOptions.length > 0 && (
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1 font-medium">Year</label>
                                    <select value={filterOptions.year} onChange={e => handleFilterChange('year', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                                        <option value="all">All Years</option>
                                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm text-gray-600 mb-1 font-medium">Piece Count</label>
                                <select value={filterOptions.pieces} onChange={e => handleFilterChange('pieces', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                                    <option value="all">Any Number</option>
                                    <option value="1-100">Small (1–100)</option>
                                    <option value="101-500">Medium (101–500)</option>
                                    <option value="501-1000">Large (501–1000)</option>
                                    <option value="1001-2000">X-Large (1001–2000)</option>
                                    <option value="2001-">Huge (2001+)</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                            <div className="text-sm text-gray-600">
                                <span className="font-medium">{filteredSets.length}</span> sets shown
                            </div>
                            <button onClick={resetFilters}
                                className="px-4 py-1 text-red-600 hover:text-red-700 text-sm font-medium focus:outline-none">
                                Reset Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* Bulk action bar */}
                {getSelectedSetCount() > 0 && (
                    <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center mr-3">
                                    <FontAwesomeIcon icon="shopping-basket" />
                                </div>
                                <span className="text-gray-800 font-medium">
                                    {getSelectedSetCount()} {getSelectedSetCount() === 1 ? 'set' : 'sets'} selected
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                                    onClick={e => addToCollection(null, null, e)}>
                                    <FontAwesomeIcon icon="plus" />
                                    <span>Add to Collection</span>
                                </button>
                                <button className="px-3 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg transition-colors flex items-center gap-2 text-sm"
                                    onClick={e => addToWishlist(null, null, e)}>
                                    <FontAwesomeIcon icon="heart" />
                                    <span>Add to Wishlist</span>
                                </button>
                                <button className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm"
                                    onClick={clearSelectedSets}>
                                    <FontAwesomeIcon icon="times" />
                                    <span>Clear</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderSubThemes = () => {
        if (subThemes.length === 0) return null;
        return (
            <div className="mb-8 bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="p-4 md:p-6 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon="folder-open" className="text-red-600 mr-3" />
                        Sub-Themes
                    </h2>
                </div>
                <div className="p-4 md:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {subThemes.map(theme => (
                            <button
                                key={theme.id}
                                className="flex flex-col items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md hover:border-red-200 hover:bg-red-50/30 group"
                                onClick={() => navigate(`/themes/${theme.id}`)}
                            >
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-red-100 transition-colors">
                                    <FontAwesomeIcon icon="cubes" className="text-gray-600 group-hover:text-red-600 transition-colors" />
                                </div>
                                <span className="text-center font-medium text-gray-800 group-hover:text-red-600 transition-colors">
                                    {theme.name}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSets = () => {
        if (isLoading) {
            return <div className="py-16"><LoadingSpinner text="Loading sets..." size="lg" /></div>;
        }
        if (error) {
            return <ErrorMessage message={error} onRetry={() => setError(null)} />;
        }
        if (filteredSets.length === 0) {
            return (
                <EmptyState
                    message={hasActiveFilter() ? 'No sets found matching your filters.' : 'No sets found for this theme.'}
                    actionText={hasActiveFilter() ? 'Reset Filters' : null}
                    onAction={resetFilters}
                    icon="search"
                    className="py-12"
                />
            );
        }

        return (
            <div className="mb-8 bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="p-4 md:p-6 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon="cube" className="text-red-600 mr-3" />
                        Sets
                        <span className="ml-2 text-base font-normal text-gray-500">
                            ({filteredSets.length} shown
                            {hasMore ? `, ${totalSets > 0 ? totalSets.toLocaleString() : '?'} total` : ''})
                        </span>
                    </h2>
                </div>

                <div className="p-4 md:p-6">
                    {activeView === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {filteredSets.map(set => (
                                <SetCard
                                    key={set.set_num}
                                    set={set}
                                    status={collectionStatus[set.set_num] ?? null}
                                    viewMode="grid"
                                    showDetails
                                    selectedQuantity={selectedSets[set.set_num]}
                                    onToggleSelect={toggleSelectSet}
                                    onQuantityChange={handleQuantityChange}
                                    onAddToCollection={addToCollection}
                                    onAddToWishlist={addToWishlist}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {filteredSets.map(set => (
                                <SetCard
                                    key={set.set_num}
                                    set={set}
                                    status={collectionStatus[set.set_num] ?? null}
                                    viewMode="list"
                                    showDetails
                                    selectedQuantity={selectedSets[set.set_num]}
                                    onToggleSelect={toggleSelectSet}
                                    onQuantityChange={handleQuantityChange}
                                    onAddToCollection={addToCollection}
                                    onAddToWishlist={addToWishlist}
                                />
                            ))}
                        </div>
                    )}

                    {isLoadingMore && (
                        <div className="text-center py-8">
                            <div className="inline-block h-8 w-8 simple-spinner mx-auto" />
                            <p className="mt-2 text-sm text-gray-600">Loading more sets...</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // -------------------------------------------------------------------------
    // Root render
    // -------------------------------------------------------------------------

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4">
                {renderThemeHeader()}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onConfirm={modalContent.onConfirm}
                    title={modalContent.title}
                    confirmText={modalContent.confirmText || 'Confirm'}
                >
                    {modalContent.message}
                </Modal>

                {!isLoading && renderSubThemes()}
                {!isLoading && sets.length > 0 && renderSearchAndFilters()}
                {renderSets()}
            </div>
        </div>
    );
};

export default SubThemes;
